import {
  useRef,
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import { Box, Text } from "@mantine/core";

export interface SignaturePadRef {
  /** Returns the signature as a PNG data URL, or null if empty */
  toDataURL: () => string | null;
  /** Clears the canvas */
  clear: () => void;
  /** Whether the canvas is empty */
  isEmpty: () => boolean;
}

interface SignaturePadProps {
  height?: number;
}

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  ({ height = 150 }, ref) => {
    const sigRef = useRef<SignatureCanvas>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [canvasWidth, setCanvasWidth] = useState(400);

    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          const w = containerRef.current.clientWidth;
          if (w > 0) setCanvasWidth(w);
        }
      };
      updateWidth();
      const ro = new ResizeObserver(updateWidth);
      if (containerRef.current) ro.observe(containerRef.current);
      return () => ro.disconnect();
    }, []);

    useImperativeHandle(ref, () => ({
      toDataURL: () => {
        if (!sigRef.current || sigRef.current.isEmpty()) return null;
        return sigRef.current.toDataURL("image/png");
      },
      clear: () => sigRef.current?.clear(),
      isEmpty: () => sigRef.current?.isEmpty() ?? true,
    }));

    return (
      <Box>
        <Text size="sm" fw={500} mb={4}>
          Draw your signature
        </Text>
        <Box
          ref={containerRef}
          style={{
            border: "1px solid #dee2e6",
            borderRadius: 4,
            backgroundColor: "#fff",
            cursor: "crosshair",
          }}
        >
          <SignatureCanvas
            ref={sigRef}
            canvasProps={{
              width: canvasWidth,
              height,
              style: { width: canvasWidth, height, display: "block" },
            }}
            penColor="black"
            backgroundColor="rgba(255,255,255,0)"
          />
        </Box>
      </Box>
    );
  }
);
